import {
  SanityDisclaimerPage,
  SanityFileLink,
  SanityTwoStepDisclaimer,
} from '../../types/generated/graphql-types'
import { DisclaimerRouteProps } from '../components/Disclaimer/DisclaimerRoute'
import { TwoStepDisclaimerProps } from '../components/Disclaimer/SiteWide/TwoStepDisclaimer'
import { notEmpty } from '../utilities/filters'
import { getUrlFragments, SanityNode } from '../utilities/routing'
import { isNullOrWhiteSpace } from '../utilities/string'
import {
  mapSanityAttributeArrayToCustomAttributes,
  mapSanityLinkToInternalExternalLink,
} from './LinkMapper'

export const sanityDisclaimerPageToDisclaimerRouteProps = (
  disclaimerPage?: SanityDisclaimerPage
): DisclaimerRouteProps => {
  let disclaimerPath: string | undefined

  if (disclaimerPage) {
    disclaimerPath = disclaimerPage?.path ?? undefined

    // Fallback to getUrlFragments() if path is missing, since it may not exist for on demand press releases.
    if (isNullOrWhiteSpace(disclaimerPath)) {
      const disclaimerUrlFragments = getUrlFragments(disclaimerPage as SanityNode)
      disclaimerPath = `/${disclaimerUrlFragments.join('/')}`
    }
  }

  return {
    disclaimerPagePath: disclaimerPath,
  }
}

export const sanityTwoStepDisclaimerToTwoStepDisclaimerProps = (
  disclaimer: SanityTwoStepDisclaimer
): TwoStepDisclaimerProps => {
  const { annexOne, introduction, noAccessNotice, nonProfessionals, simpleDisclaimer } =
    disclaimer
  return {
    annexOne: {
      acceptTermsButtonLabel: annexOne?.acceptTermsButtonLabel ?? '',
      acceptTermsCheckboxLabel: annexOne?.acceptTermsCheckboxLabel ?? '',
      acceptTermsText: annexOne?.acceptTermsText ?? '',
      body: annexOne?._rawBody ?? undefined,
      declineTermsButtonLabel: annexOne?.declineTermsButtonLabel ?? '',
      declineTermsUrl: annexOne?.declineTermsUrl ?? '',
      title: annexOne?.title ?? undefined,
    },
    introduction: {
      countries:
        introduction?.countries?.filter(notEmpty).map(country => ({
          eligibilityConfirmationText:
            country._rawEligibilityConfirmationText ?? undefined,
          name: country.name ?? '',
          nonProfessionalAccess: country.nonProfessionalAccess ?? false,
          nonProfessionalFullAccess: country.nonProfessionalFullAccess ?? false,
          professionalAccess: country.professionalAccess ?? false,
          professionalDisclaimerBody: country._rawProfessionalDisclaimerBody ?? undefined,
          professionalDisclaimerTitle: country.professionalDisclaimerTitle ?? undefined,
          simpleAccess: country.simpleAccess ?? false,
          nonProfessionalRedirectAccessBody:
            country._rawNonProfessionalRedirectAccessText ?? undefined,
          professionalRedirectAccessBody:
            country._rawProfessionalRedirectAccessText ?? undefined,
        })) ?? [],
      countriesIntro: introduction?.countriesIntro ?? undefined,
      disclaimerBody: introduction?._rawDisclaimerBody ?? undefined,
      investorIntro: introduction?.investorIntro ?? undefined,
      nonProfessionalInvestorBulletPoints:
        introduction?.nonProfessionalInvestorBulletPoints?.filter(notEmpty) ?? undefined,
      nonProfessionalInvestorLabel: introduction?.nonProfessionalInvestorLabel ?? '',
      preamble: introduction?.preamble ?? undefined,
      preambleLinks:
        introduction?.preambleLinks?.filter(notEmpty).map(link => {
          if (link._type === 'fileLink') {
            const fileLink = link as SanityFileLink
            const asset = fileLink.file?.asset
            let url: string | undefined | null

            if (asset) {
              url = asset?.url

              if (asset.originalFilename) {
                url = `${url}?dl=${asset.originalFilename}`
              } else {
                url = `${url}?dl`
              }
            }

            const customAttributes = mapSanityAttributeArrayToCustomAttributes(
              fileLink.customAttributes?.filter(notEmpty) ?? []
            )

            let title: string | undefined | null

            if (!isNullOrWhiteSpace(fileLink.title)) {
              title = fileLink.title
            } else if (asset) {
              if (!isNullOrWhiteSpace(asset.title)) {
                title = asset.title
              } else {
                title = asset.originalFilename
              }
            }

            return {
              customAttributes,
              title: title ?? '',
              url: url ?? '',
            }
          } else {
            return mapSanityLinkToInternalExternalLink(link)
          }
        }) ?? undefined,
      privacyPolicyLabel:
        introduction?.privacyPolicyLabel ??
        introduction?.privacyPolicyPage?.title ??
        undefined,
      privacyPolicyPageUrl: introduction?.privacyPolicyPage?.path ?? undefined,
      professionalInvestorBulletPoints:
        introduction?.professionalInvestorBulletPoints?.filter(notEmpty) ?? undefined,
      professionalInvestorLabel: introduction?.professionalInvestorLabel ?? '',
      selectCountryLabel: introduction?.selectCountryLabel ?? undefined,
      selectCountryPlaceholder: introduction?.selectCountryPlaceholder ?? undefined,
      selectInvestorPlaceholder: introduction?.selectInvestorPlaceholder ?? undefined,
      selectInvestorLabel: introduction?.selectInvestorLabel ?? undefined,
      submitButtonLabel: introduction?.submitButtonLabel ?? '',
      title: introduction?.title ?? undefined,
    },
    noAccessNotice: {
      body: noAccessNotice?._rawBody ?? undefined,
      buttonLabel: noAccessNotice?.buttonLabel ?? undefined,
      buttonUrl: annexOne?.declineTermsUrl ?? undefined,
      title: noAccessNotice?.title ?? undefined,
    },
    nonProfessionals: {
      accessiblePageUrl: nonProfessionals?.accessiblePage?.path ?? '',
      confirmEligibilityButtonLabel:
        nonProfessionals?.confirmEligibilityButtonLabel ?? '',
      declineEligibilityButtonLabel:
        nonProfessionals?.declineEligibilityButtonLabel ?? '',
      title: nonProfessionals?.title ?? undefined,
    },
    simpleVariant: simpleDisclaimer ?? false,
  }
}
